import { useAccountList } from '@ltvco/refresh-lib/hooks';
import {
  Button,
  Divider,
  Popover,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { useReportMonitors } from '@ltvco/refresh-lib/v1';
import {
  LizanoListInfo,
  generateQueryConfig,
  stringifyParams,
  useRecentReports,
  type ReportType,
} from '@ltvco/refresh-lib/v2';
import { DeleteOutline, EditOutlined } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import { ChatPageContainerStack } from 'components/ChatBV/components/ChatPage/ChatComponents/components';
import { ListViewComponent } from 'components/ChatBV/components/ReportLibrary/Lists/components/ListView/ListViewComponent';
import { DEEPSEARCH_REPORT_TYPES_MAP } from 'components/ChatBV/constants';
import emptyListImage from 'images/img_empty_list.png';

import { DeleteListConfirmation } from './DeleteListConfirmation';
import { EmptyState } from './EmptyState';
import { ListForm } from './ListForm';
import { MoreWaysSection } from './MoreWaysSection';
import { ActionIconButton, ReportSection } from './ReportSection';
import {
  dateParser,
  getListReportCount,
  headerButtonElementRecentReports,
} from './utils/utils';

const chatbvReportTypes = Object.values(
  DEEPSEARCH_REPORT_TYPES_MAP
) as ReportType[];
const queryConfig = generateQueryConfig();

interface ReportLibraryLandingPageProps {
  basePath?: string;
}

export const ReportLibraryLandingPage = ({
  basePath = '/chatbv/chat',
}: ReportLibraryLandingPageProps) => {
  const navigate = useNavigate();
  const {
    breadcrumbTitle: breadcrumbTitleFromPath,
    listCode: listCodeFromPath,
  } = useParams<{
    breadcrumbTitle?: string;
    listCode?: string;
  }>();
  const theme = useTheme();

  const [queryParams] = useQueryParams(queryConfig);
  const recentReportsParams = {
    ...stringifyParams(queryParams),
    generator: 'chat',
  };
  const { data: recentReportsData } = useRecentReports(recentReportsParams);
  const {
    data: lists,
    isLoading: isListsLoading,
    updateList,
    createList,
  } = useAccountList();
  const { isMobile } = useScreenSize();
  const { currentMonitors } = useReportMonitors();

  const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [editAnchorEl, setEditAnchorEl] = useState<HTMLElement | null>(null);
  const [createAnchorEl, setCreateAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [activeListCode, setActiveListCode] = useState<string | null>(null);
  const [listViewLists, setListViewLists] = useState<LizanoListInfo[] | null>(
    null
  );
  const [listViewRecentOpen, setListViewRecentOpen] = useState(false);

  const [breadcrumbTitle, setBreadcrumbTitle] = useState<string>('');

  const recentReportsCount =
    recentReportsData?.meta?.report_quantities?.filtered_reports_count || 0;
  const filteredMonitors = (currentMonitors ?? []).filter((m) =>
    chatbvReportTypes.includes(m.report_type)
  );
  const monitoredReportsCount = filteredMonitors.length;

  const savedReportsList = lists?.[0];
  const createdLists = lists?.slice(1) || [];

  const recentReports =
    (recentReportsData?.reports as Array<{ updatedAt?: string }>) || [];
  const recentReportsLastUpdatedDate = recentReports.length
    ? dateParser.parseDateFromString(
        recentReports.reduce((latest, report) => {
          const d = report.updatedAt || '';
          return d > latest ? d : latest;
        }, ''),
        'L/dd/yyyy'
      )
    : undefined;

  const savedReportsLastUpdatedDate = savedReportsList?.updated_at
    ? dateParser.parseDateFromString(savedReportsList.updated_at, 'L/dd/yyyy')
    : undefined;

  const monitoredReportsLastUpdatedDate = filteredMonitors.length
    ? dateParser.parseDateFromString(
        filteredMonitors.reduce((latest, monitor) => {
          const d = monitor.updated_at;
          return d && d > latest ? d : latest;
        }, filteredMonitors[0]?.updated_at || ''),
        'L/dd/yyyy'
      )
    : undefined;

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLElement>,
    listCode: string
  ) => {
    event.stopPropagation();
    setActiveListCode(listCode);
    setDeleteAnchorEl(event.currentTarget);
  };

  const handleEditClick = (
    event: React.MouseEvent<HTMLElement>,
    list: LizanoListInfo
  ) => {
    event.stopPropagation();
    setActiveListCode(list.list_code);
    setEditAnchorEl(event.currentTarget);
  };

  const handleCreateListClick = (event: React.MouseEvent<HTMLElement>) => {
    setCreateAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setDeleteAnchorEl(null);
    setEditAnchorEl(null);
    setCreateAnchorEl(null);
    setActiveListCode(null);
  };

  const currentListNames =
    lists?.map((list) => list.name.toLocaleLowerCase()) || [];

  const listBeingEdited =
    activeListCode && lists
      ? lists.find((list) => list.list_code === activeListCode)
      : undefined;

  const hasNoCustomLists = createdLists.length === 0;

  useEffect(() => {
    if (!listViewLists && !listViewRecentOpen) {
      document.title = 'Report Library - BeenVerified';
    }
  }, [listViewLists, listViewRecentOpen]);

  useEffect(() => {
    if (listCodeFromPath) {
      let decodedListCode: string;
      try {
        decodedListCode = decodeURIComponent(listCodeFromPath);
      } catch {
        return;
      }
      const matchedCustomList = (lists?.slice(1) ?? []).find(
        (list) => list.list_code === decodedListCode
      );
      if (matchedCustomList) {
        setListViewRecentOpen(false);
        setListViewLists([matchedCustomList]);
        setBreadcrumbTitle(matchedCustomList.name);
      }
      return;
    }

    if (!breadcrumbTitleFromPath) {
      return;
    }

    let title: string;
    try {
      title = decodeURIComponent(breadcrumbTitleFromPath);
    } catch {
      return;
    }

    if (title === 'Recent Reports') {
      setListViewLists(null);
      setListViewRecentOpen(true);
      setBreadcrumbTitle(title);
      return;
    }

    if (title === 'Saved Reports') {
      if (!savedReportsList) {
        return;
      }
      setListViewRecentOpen(false);
      setListViewLists([savedReportsList]);
      setBreadcrumbTitle(title);
    }
  }, [listCodeFromPath, breadcrumbTitleFromPath, savedReportsList, lists]);

  const exitListView = () => {
    setListViewLists(null);
    setListViewRecentOpen(false);
    navigate(`${basePath}/library`, { replace: true });
  };

  const showListViewComponent = () => {
    const listViewHeaderButtonElement = headerButtonElementRecentReports({
      navigate,
      isMobile,
      breadcrumbTitle,
      onSettingsClick: () => navigate('/dashboard/id-monitor'),
      onAddClick: () => navigate(basePath),
      onImportClick: () => navigate(`${basePath}/library`),
    });

    if (listViewRecentOpen) {
      return (
        <Stack
          padding={2.5}
          paddingTop={1}
          width="100%"
          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
        >
          <ListViewComponent
            lists={[]}
            recentReports={[]}
            onBack={exitListView}
            breadcrumbTitle={breadcrumbTitle}
            headerButtonElement={listViewHeaderButtonElement}
            basePath={basePath}
          />
        </Stack>
      );
    }

    if (listViewLists) {
      return (
        <Stack
          padding={2.5}
          paddingTop={1}
          width="100%"
          sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
        >
          <ListViewComponent
            lists={listViewLists}
            onBack={exitListView}
            breadcrumbTitle={breadcrumbTitle}
            headerButtonElement={listViewHeaderButtonElement}
            basePath={basePath}
          />
        </Stack>
      );
    }

    return null;
  };

  const listViewContent = showListViewComponent();
  if (listViewContent) {
    return (
      <ChatPageContainerStack
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        {listViewContent}
      </ChatPageContainerStack>
    );
  }

  return (
    <ChatPageContainerStack
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <Stack padding={2.5} paddingTop={1} width="100%">
        <Text variant="h2" fontWeight="bold" mt={0} mb={1}>
          Report Library
        </Text>
        <Stack direction="row" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            onClick={handleCreateListClick}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3,
            }}
          >
            Create List
          </Button>
        </Stack>

        <Divider sx={{ borderColor: theme.palette.divider }} />

        <Stack>
          <ReportSection
            title="Recent Reports"
            subtitle={
              recentReportsLastUpdatedDate
                ? `Updated ${recentReportsLastUpdatedDate}`
                : undefined
            }
            count={recentReportsCount}
            onClick={() => {
              const title = 'Recent Reports';
              navigate(`${basePath}/library/${encodeURIComponent(title)}`);
            }}
          />
          <Divider sx={{ borderColor: theme.palette.divider }} />
          <ReportSection
            title="Saved Reports"
            subtitle={
              savedReportsLastUpdatedDate
                ? `Updated ${savedReportsLastUpdatedDate}`
                : undefined
            }
            count={savedReportsList ? getListReportCount(savedReportsList) : 0}
            onClick={() => {
              if (savedReportsList) {
                const title = 'Saved Reports';
                setListViewRecentOpen(false);
                navigate(`${basePath}/library/${encodeURIComponent(title)}`);
              }
            }}
          />
          <Divider sx={{ borderColor: theme.palette.divider }} />
          <ReportSection
            title="Monitored Reports"
            subtitle={
              monitoredReportsLastUpdatedDate
                ? `Updated ${monitoredReportsLastUpdatedDate}`
                : undefined
            }
            count={monitoredReportsCount}
            onClick={() => {
              navigate(`${basePath}/library/monitored`);
            }}
          />
          {createdLists.map((createdList) => (
            <React.Fragment key={createdList.list_code}>
              <Divider sx={{ borderColor: theme.palette.divider }} />
              <ReportSection
                title={createdList.name}
                subtitle={`Updated ${dateParser.parseDateFromString(
                  createdList.updated_at,
                  'L/dd/yyyy'
                )}`}
                count={getListReportCount(createdList)}
                onClick={() => {
                  setListViewRecentOpen(false);
                  navigate(
                    `${basePath}/library/list/${encodeURIComponent(
                      createdList.list_code
                    )}`
                  );
                }}
                actions={
                  <Stack direction="row" spacing={0.5}>
                    <ActionIconButton
                      aria-label="delete list"
                      onClick={(e) =>
                        handleDeleteClick(e, createdList.list_code)
                      }
                    >
                      <DeleteOutline fontSize="small" />
                    </ActionIconButton>
                    <ActionIconButton
                      aria-label="edit list"
                      onClick={(e) => handleEditClick(e, createdList)}
                    >
                      <EditOutlined fontSize="small" />
                    </ActionIconButton>
                  </Stack>
                }
              />
            </React.Fragment>
          ))}
        </Stack>

        <Popover
          open={Boolean(deleteAnchorEl)}
          anchorEl={deleteAnchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {activeListCode && (
            <DeleteListConfirmation
              setAnchorEl={handleClosePopover}
              listCode={activeListCode}
            />
          )}
        </Popover>

        <Popover
          open={Boolean(editAnchorEl)}
          anchorEl={editAnchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {activeListCode && (
            <ListForm
              closeForm={handleClosePopover}
              title="Edit list"
              mainActionButtonText="Update"
              placeholderText="List name"
              listCode={activeListCode}
              action={updateList}
              currentListNames={currentListNames}
              originalListName={listBeingEdited?.name}
            />
          )}
        </Popover>

        <Popover
          open={Boolean(createAnchorEl)}
          anchorEl={createAnchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <ListForm
            closeForm={handleClosePopover}
            title="Create new list"
            mainActionButtonText="Create"
            placeholderText="List name"
            action={createList}
            currentListNames={currentListNames}
          />
        </Popover>

        {!isListsLoading && hasNoCustomLists && (
          <EmptyState
            image={emptyListImage}
            title="No list created yet."
            description="Start by creating list using the Create List button"
            actionLabel="Create List"
            onAction={handleCreateListClick}
          >
            <MoreWaysSection />
          </EmptyState>
        )}
      </Stack>
    </ChatPageContainerStack>
  );
};
